import type { RealtimeEvent } from "./types";

/**
 * A vendor-neutral reference to the aggregate a frame is about — mirrors the backend `EntityRef`
 * (DOMAIN.md §B.5/§D.3). `type` is the closed `EntityType` vocabulary (e.g. "BOOKING"), `id` its
 * canonical id string.
 */
export interface EntityRef {
  type: string;
  id: string;
}

/**
 * The compact realtime frame the backend fans out (tasks/realtime-sync.md): ids + version +
 * attribution ONLY — never the record body, never guest PII. `eventType` is the SSE event name
 * (e.g. "booking.modified"); `actorStaffId` (when present) lets the UI compose the FR/EN attribution
 * string from the cached staff list.
 */
export interface RealtimeFrame {
  eventId: string;
  entity: EntityRef;
  version: number;
  eventType: string;
  actorStaffId?: string;
}

/** The gate's verdict for one frame (per-record monotonicity, §D.3). */
export type AdmitResult =
  /** Strictly newer AND contiguous with the last applied version → apply (refetch). */
  | "apply"
  /** Newer but versions were skipped (e.g. v4 → v7) → apply, but a gap occurred (one targeted refetch). */
  | "gap"
  /** Not newer than the last applied (stale or duplicate) → drop; never apply stale data. */
  | "drop";

export function entityRefKey(entity: EntityRef): string {
  return `${entity.type}:${entity.id}`;
}

/**
 * Parse a raw SSE event into a {@link RealtimeFrame}, or `null` when it is not an aggregate frame
 * (a `presence.changed` / `connected` control frame carries no `version`, so it is not gated). The
 * data payload is JSON `{ eventId, entity: { type, id }, version, actorStaffId? }`.
 */
export function parseRealtimeFrame(event: RealtimeEvent): RealtimeFrame | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(event.data);
  } catch {
    return null;
  }
  if (typeof parsed !== "object" || parsed === null) return null;
  const p = parsed as Record<string, unknown>;
  const entity = p.entity as Record<string, unknown> | undefined;
  if (!entity || typeof entity.type !== "string" || typeof entity.id !== "string") return null;
  if (typeof p.version !== "number" || typeof p.eventId !== "string") return null;
  return {
    eventId: p.eventId,
    entity: { type: entity.type, id: entity.id },
    version: p.version,
    eventType: event.type,
    actorStaffId: typeof p.actorStaffId === "string" ? p.actorStaffId : undefined,
  };
}

/**
 * Per-record monotonic version gate — the CLIENT half of the backend `VersionGate` (§D.3, ADR-0055),
 * wired so a device applies only the newest state per `EntityRef`. Async delivery cannot promise
 * global order, so given frames out of order for one record only the newest wins: a frame whose
 * version is not strictly newer than the last applied is DROPPED (never applied), a forward gap is
 * still applied but reported as `"gap"` so the caller can measure/label the one targeted refetch.
 *
 * Since frames are invalidation HINTS (never truth, ADR-0012), both `"apply"` and `"gap"` mean
 * "refetch through the normal query path"; only `"drop"` means "do nothing". {@link reset} is called
 * on reconnect, where the client snapshots via a full invalidate rather than replaying.
 */
export class VersionGate {
  private readonly lastApplied = new Map<string, number>();

  admit(entity: EntityRef, version: number): AdmitResult {
    const key = entityRefKey(entity);
    const prev = this.lastApplied.get(key);
    if (prev !== undefined && version <= prev) return "drop";
    const gapped = prev !== undefined && version > prev + 1;
    this.lastApplied.set(key, version);
    return gapped ? "gap" : "apply";
  }

  /** The last version applied for [entity], or undefined if none seen yet. */
  lastVersion(entity: EntityRef): number | undefined {
    return this.lastApplied.get(entityRefKey(entity));
  }

  /** Forget all state — called on (re)connect, where the client snapshots rather than replays. */
  reset(): void {
    this.lastApplied.clear();
  }
}
